import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Check, CheckCircle2, Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorCode } from "@/lib/api";
import type { PartnerMe } from "@/hooks/useCurrentPartner";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EditorialButton } from "@/components/editorial";
import { formatCode, formatDateTZ, formatDuration, formatMoney, formatRenewalCadence } from "@/lib/format";
import { buildWelcomeMessage } from "@/lib/welcome-message";
import { cn } from "@/lib/utils";

const CODE_EXPIRES_IN_DAYS = 90;

export function popularityLabel(days: number): string {
  if (days === 360 || days === 365) return " · 🔥 Más popular";
  if (days === 90 || days === 180) return " · ⭐ Popular";
  return "";
}

type IssuedCode = {
  code: string;
  duration_days: number;
  currency: string;
  price_cents: number;
  code_expires_at: string;
  sold_to_email: string;
  sold_to_name: string;
  email_sent: boolean;
  subscription_id: number | null;
};

type Props = {
  partner: PartnerMe;
  /** Contenido/estilo custom para el trigger. Si no se pasa, usa el default. */
  trigger?: React.ReactNode;
};

export function IssueCodeDialog({ partner, trigger }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const availableDurations = partner.pricing.map((p) => p.duration_days);
  const [duration, setDuration] = useState<number>(availableDurations[0] ?? 30);
  const [isRecurring, setIsRecurring] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Cuando esto tiene un valor, mostramos la vista de éxito con el código.
  const [issued, setIssued] = useState<IssuedCode | null>(null);

  const mutation = useMutation({
    mutationFn: async (): Promise<IssuedCode> =>
      (
        await api.post<IssuedCode>("/partner/codes", {
          duration_days: duration,
          code_expires_in_days: CODE_EXPIRES_IN_DAYS,
          sold_to_email: email.trim(),
          sold_to_name: name.trim(),
          is_recurring: isRecurring,
        })
      ).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["codes"] });
      qc.invalidateQueries({ queryKey: ["subscriptions"] });
      qc.invalidateQueries({ queryKey: ["reports", "revenue"] });
      qc.invalidateQueries({ queryKey: ["revenue-report"] });
      setIssued(data);
      setErr(null);
      toast.success("Código emitido", {
        description: `${formatCode(data.code)} · ${formatDuration(data.duration_days)}`,
      });
    },
    onError: (e) => {
      const code = apiErrorCode(e);
      const msg =
        code === "PRICING_NOT_SET"
          ? "No hay precio configurado para esa duración. Contacta a NiuFit."
          : code === "COMMISSION_NOT_SET"
          ? "No hay comisión configurada. Contacta a NiuFit."
          : "No se pudo generar el código";
      setErr(msg);
      toast.error("Error al emitir código", { description: msg });
    },
  });

  const canSubmit = !!email.trim() && !!name.trim() && duration > 0 && !mutation.isPending;

  function resetAll() {
    setEmail("");
    setName("");
    setIsRecurring(false);
    setErr(null);
    setIssued(null);
  }

  function handleOpenChange(v: boolean) {
    setOpen(v);
    if (!v) resetAll();
  }

  function handleEmitAnother() {
    setIssued(null);
    setErr(null);
    setEmail("");
    setName("");
    setIsRecurring(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <EditorialButton variant="primary">
            Emitir código <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
          </EditorialButton>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        {issued ? (
          <SuccessView
            issued={issued}
            partner={partner}
            onEmitAnother={handleEmitAnother}
          />
        ) : (
          <FormView
            email={email}
            setEmail={setEmail}
            name={name}
            setName={setName}
            duration={duration}
            setDuration={setDuration}
            isRecurring={isRecurring}
            setIsRecurring={setIsRecurring}
            err={err}
            setErr={setErr}
            partner={partner}
            canSubmit={canSubmit}
            isPending={mutation.isPending}
            onSubmit={() => mutation.mutate()}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────── Form view ─────────────── */

function FormView({
  email,
  setEmail,
  name,
  setName,
  duration,
  setDuration,
  isRecurring,
  setIsRecurring,
  err,
  setErr,
  partner,
  canSubmit,
  isPending,
  onSubmit,
}: {
  email: string;
  setEmail: (v: string) => void;
  name: string;
  setName: (v: string) => void;
  duration: number;
  setDuration: (v: number) => void;
  isRecurring: boolean;
  setIsRecurring: (v: boolean) => void;
  err: string | null;
  setErr: (v: string | null) => void;
  partner: PartnerMe;
  canSubmit: boolean;
  isPending: boolean;
  onSubmit: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-xl font-semibold tracking-tight">Emitir código</DialogTitle>
        <DialogDescription>
          Se enviará por email al comprador con instrucciones para canjearlo en la app NiuFit.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 mt-2">
        <Field label="Nombre del comprador">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={150} />
        </Field>
        <Field
          label="Email del comprador"
          hint="Sólo para enviarle el código. No tiene que coincidir con el que use en la app."
        >
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="cliente@correo.com"
          />
        </Field>
        <Field label="Plan">
          {partner.pricing.length > 0 ? (
            <select
              className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm focus:outline-none focus:border-neutral-400"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            >
              {partner.pricing.map((p) => (
                <option key={p.duration_days} value={p.duration_days}>
                  {formatDuration(p.duration_days)} · {p.duration_days} días · {formatMoney(p.price_cents, partner.currency)}
                  {popularityLabel(p.duration_days)}
                </option>
              ))}
            </select>
          ) : (
            <Input
              type="number"
              min={1}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
          )}
        </Field>
        <p className="text-xs text-neutral-500">
          El comprador tendrá{" "}
          <span className="font-medium text-neutral-700">{CODE_EXPIRES_IN_DAYS} días</span>{" "}
          para canjear el código. Después de ese plazo, expira sin devolución.
        </p>

        <label className="flex items-start gap-3 rounded-md border border-neutral-200 p-3 cursor-pointer hover:bg-neutral-50">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-neutral-900"
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e.target.checked)}
          />
          <div className="text-sm">
            <div className="font-medium">Suscripción recurrente</div>
            <p className="text-xs text-neutral-500 mt-0.5">
              Se renovará automáticamente {formatRenewalCadence(duration)} con el mismo precio. Pausable en cualquier momento.
            </p>
          </div>
        </label>

        {err && <p className="text-sm text-destructive">{err}</p>}
      </div>

      <DialogFooter className="mt-4 gap-2">
        <DialogClose asChild>
          <EditorialButton variant="ghost">Cancelar</EditorialButton>
        </DialogClose>
        <EditorialButton
          variant="primary"
          disabled={!canSubmit}
          onClick={() => {
            setErr(null);
            onSubmit();
          }}
        >
          {isPending ? "Emitiendo" : "Emitir y enviar"}
          <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
        </EditorialButton>
      </DialogFooter>
    </>
  );
}

/* ─────────────── Success view (recibo con código y botones de copiar) ─────────────── */

function SuccessView({
  issued,
  partner,
  onEmitAnother,
}: {
  issued: IssuedCode;
  partner: PartnerMe;
  onEmitAnother: () => void;
}) {
  const formattedCode = formatCode(issued.code);
  const [copied, setCopied] = useState<null | "code" | "welcome">(null);

  async function handleCopy(kind: "code" | "welcome") {
    const text =
      kind === "code"
        ? formattedCode
        : buildWelcomeMessage({
            code: issued.code,
            sold_to_name: issued.sold_to_name,
            duration_days: issued.duration_days,
            price_cents: issued.price_cents,
            currency: issued.currency,
            code_expires_at: issued.code_expires_at,
            partnerName: partner.name,
            partnerTimezone: partner.timezone,
          });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 2000);
      toast.success(kind === "code" ? "Código copiado" : "Mensaje de WhatsApp copiado");
    } catch {
      toast.error("No se pudo copiar. Selecciona el texto manualmente.");
    }
  }

  return (
    <>
      <DialogHeader>
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-emerald-50 p-2 border border-emerald-100 flex-shrink-0">
            <CheckCircle2 className="h-5 w-5 text-emerald-700" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <DialogTitle className="text-xl font-semibold tracking-tight">
              Código emitido
            </DialogTitle>
            <DialogDescription className="mt-1">
              {issued.email_sent
                ? `Enviado por email a ${issued.sold_to_email}. Cópialo también para mandarlo por WhatsApp.`
                : "El email no pudo enviarse — copia el código y compártelo manualmente."}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      {/* Código destacado */}
      <div className="mt-4 rounded-lg border border-neutral-200 bg-neutral-50 px-6 py-6 text-center">
        <div className="text-[10px] font-mono-num uppercase tracking-[0.22em] text-neutral-500">
          Código de activación
        </div>
        <div className="mt-2 font-mono-num tabular-nums text-3xl font-semibold text-neutral-900 tracking-[0.08em]">
          {formattedCode}
        </div>
      </div>

      {/* Botones de copiar */}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <button
          type="button"
          onClick={() => handleCopy("code")}
          className={cn(
            "inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-md border text-sm font-medium transition-colors whitespace-nowrap",
            copied === "code"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50"
          )}
        >
          {copied === "code" ? (
            <Check className="h-4 w-4 flex-shrink-0" strokeWidth={2.25} />
          ) : (
            <Copy className="h-4 w-4 flex-shrink-0" strokeWidth={2} />
          )}
          {copied === "code" ? "Copiado" : "Solo código"}
        </button>
        <button
          type="button"
          onClick={() => handleCopy("welcome")}
          className={cn(
            "inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-md border text-sm font-medium transition-colors whitespace-nowrap",
            copied === "welcome"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-neutral-200 bg-white text-neutral-700 hover:border-emerald-200 hover:bg-emerald-50/40"
          )}
        >
          {copied === "welcome" ? (
            <Check className="h-4 w-4 flex-shrink-0" strokeWidth={2.25} />
          ) : (
            <MessageCircle className="h-4 w-4 text-emerald-700 flex-shrink-0" strokeWidth={2} />
          )}
          {copied === "welcome" ? "Copiado" : "Mensaje WhatsApp"}
        </button>
      </div>

      {/* Metadata */}
      <dl className="mt-4 space-y-1.5 text-xs">
        <div className="flex justify-between">
          <dt className="text-neutral-500">Comprador</dt>
          <dd className="text-neutral-900 font-medium">{issued.sold_to_name}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-neutral-500">Email</dt>
          <dd className="text-neutral-900 font-medium truncate ml-3">{issued.sold_to_email}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-neutral-500">Plan</dt>
          <dd className="text-neutral-900 font-medium">
            {formatDuration(issued.duration_days)} · {formatMoney(issued.price_cents, issued.currency)}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-neutral-500">Vence para canjear</dt>
          <dd className="text-neutral-900 font-medium">
            {formatDateTZ(issued.code_expires_at, partner.timezone)}
          </dd>
        </div>
        {issued.subscription_id && (
          <div className="flex justify-between">
            <dt className="text-neutral-500">Recurrencia</dt>
            <dd className="text-emerald-700 font-medium">Suscripción activa</dd>
          </div>
        )}
      </dl>

      <DialogFooter className="mt-5 gap-2">
        <DialogClose asChild>
          <EditorialButton variant="ghost">Cerrar</EditorialButton>
        </DialogClose>
        <EditorialButton variant="primary" onClick={onEmitAnother}>
          Emitir otro código
          <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
        </EditorialButton>
      </DialogFooter>
    </>
  );
}

/* ─────────────── Field helper ─────────────── */

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-neutral-700">{label}</Label>
      {children}
      {hint && <p className="text-xs text-neutral-500">{hint}</p>}
    </div>
  );
}
